import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building2, 
  User, 
  Mail, 
  Phone, 
  FileText, 
  Target, 
  Palette, 
  Globe, 
  Instagram, 
  Facebook,
  MapPin,
  MessageSquare,
  Check,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STYLE_OPTIONS = [
  "Minimalista y elegante",
  "Moderno y colorido",
  "Corporativo y profesional",
  "Creativo y único",
  "Clásico y sofisticado"
];

const INDUSTRY_OPTIONS = [
  "Clínica Estética",
  "Clínica Veterinaria",
  "Gimnasio / Fitness",
  "Restaurante / Café",
  "Barbería",
  "Salón de Belleza",
  "Academia / Educación",
  "Consultorio Médico",
  "Tienda / Retail",
  "Servicios Profesionales",
  "Otro"
];

export default function OnboardingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const method = searchParams.get('method');
  
  const [formData, setFormData] = useState({
    order_id: orderId || '',
    session_id: sessionId || '',
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    business_description: '',
    services: '',
    target_audience: '',
    preferred_colors: '',
    style_preferences: '',
    reference_websites: '',
    instagram: '',
    facebook: '',
    google_maps_link: '',
    additional_notes: ''
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/api/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubmitted(true);
        toast.success('¡Información enviada correctamente!');
      } else {
        toast.error('Error al enviar la información');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const isStepValid = (step) => {
    switch(step) {
      case 1:
        return formData.business_name && formData.contact_name && formData.email && formData.phone;
      case 2:
        return formData.business_description && formData.services && formData.target_audience;
      case 3:
        return true; // Optional fields
      default:
        return true;
    }
  };

  const totalSteps = 3;

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-lg"
        >
          <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            ¡Información Recibida!
          </h1>
          
          <p className="text-lg text-zinc-400 mb-8">
            Gracias por completar el formulario de onboarding. Nuestro equipo revisará 
            tu información y te contactará en las próximas 24 horas para comenzar con tu proyecto.
          </p>
          
          <div className="bg-[#0A0A0A] border border-white/10 p-6 mb-8 text-left">
            <h3 className="font-bold mb-4">Próximos pasos:</h3>
            <ul className="space-y-3 text-zinc-400">
              <li className="flex items-start gap-3">
                <span className="text-white font-bold">1.</span>
                Revisaremos tu información y referencias
              </li>
              <li className="flex items-start gap-3">
                <span className="text-white font-bold">2.</span>
                Te contactaremos para una breve llamada de introducción
              </li>
              <li className="flex items-start gap-3">
                <span className="text-white font-bold">3.</span>
                Crearemos el primer borrador de tu página web
              </li>
              <li className="flex items-start gap-3">
                <span className="text-white font-bold">4.</span>
                Iteraremos juntos hasta que estés 100% satisfecho
              </li>
            </ul>
          </div>
          
          <Button
            onClick={() => navigate('/')}
            className="bg-white text-black hover:bg-zinc-200 rounded-none px-8 py-4 text-sm font-bold tracking-widest uppercase"
          >
            Volver al Inicio
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <a href="/" className="text-2xl font-bold tracking-tight inline-block mb-6">
            AG Collective
          </a>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Cuéntanos sobre tu negocio
          </h1>
          <p className="text-zinc-400">
            Esta información nos ayudará a crear la página web perfecta para tu negocio.
          </p>
        </motion.div>

        {/* Progress Bar */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors ${
                  currentStep >= step ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-500'
                }`}>
                  {currentStep > step ? <Check className="w-5 h-5" /> : step}
                </div>
                {step < 3 && (
                  <div className={`w-24 md:w-32 h-1 mx-2 transition-colors ${
                    currentStep > step ? 'bg-white' : 'bg-zinc-800'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-zinc-500">
            <span>Información Básica</span>
            <span>Tu Negocio</span>
            <span>Preferencias</span>
          </div>
        </div>

        {/* Form Steps */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="bg-[#0A0A0A] border border-white/10 p-8"
        >
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-3">
                <Building2 className="w-6 h-6" />
                Información Básica
              </h2>
              
              <div>
                <label className="block text-sm text-zinc-500 mb-2">Nombre del Negocio *</label>
                <input
                  type="text"
                  value={formData.business_name}
                  onChange={(e) => updateField('business_name', e.target.value)}
                  placeholder="Ej: Clínica Dental Sonrisa"
                  className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors"
                  data-testid="input-business-name"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-500 mb-2">Nombre de Contacto *</label>
                <input
                  type="text"
                  value={formData.contact_name}
                  onChange={(e) => updateField('contact_name', e.target.value)}
                  placeholder="Tu nombre completo"
                  className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors"
                  data-testid="input-contact-name"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-500 mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="tu@email.com"
                    className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-500 mb-2">Teléfono / WhatsApp *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="+57 300 123 4567"
                    className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors"
                    data-testid="input-phone"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Business Details */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-3">
                <FileText className="w-6 h-6" />
                Sobre tu Negocio
              </h2>
              
              <div>
                <label className="block text-sm text-zinc-500 mb-2">Descripción del Negocio *</label>
                <textarea
                  value={formData.business_description}
                  onChange={(e) => updateField('business_description', e.target.value)}
                  placeholder="Cuéntanos sobre tu negocio, qué lo hace especial, cuánto tiempo llevas operando..."
                  rows={4}
                  className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors resize-none"
                  data-testid="input-description"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-500 mb-2">Servicios que Ofreces *</label>
                <textarea
                  value={formData.services}
                  onChange={(e) => updateField('services', e.target.value)}
                  placeholder="Lista tus principales servicios o productos, con precios si los quieres mostrar..."
                  rows={4}
                  className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors resize-none"
                  data-testid="input-services"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-500 mb-2">Público Objetivo *</label>
                <input
                  type="text"
                  value={formData.target_audience}
                  onChange={(e) => updateField('target_audience', e.target.value)}
                  placeholder="Ej: Mujeres 25-45 años en El Poblado, familias con mascotas..."
                  className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors"
                  data-testid="input-target"
                />
              </div>
            </div>
          )}

          {/* Step 3: Design Preferences */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-3">
                <Palette className="w-6 h-6" />
                Preferencias de Diseño
              </h2>
              
              <div>
                <label className="block text-sm text-zinc-500 mb-2">Colores Preferidos</label>
                <input
                  type="text"
                  value={formData.preferred_colors}
                  onChange={(e) => updateField('preferred_colors', e.target.value)}
                  placeholder="Ej: Azul y blanco, los colores de mi logo..."
                  className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors"
                  data-testid="input-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-500 mb-3">Estilo Preferido</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {STYLE_OPTIONS.map((style) => (
                    <button
                      key={style}
                      type="button"
                      onClick={() => updateField('style_preferences', style)}
                      className={`p-3 text-left border transition-colors ${
                        formData.style_preferences === style
                          ? 'border-white bg-white/10'
                          : 'border-white/20 hover:border-white/40'
                      }`}
                    >
                      {style}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-500 mb-2">Sitios Web de Referencia</label>
                <textarea
                  value={formData.reference_websites}
                  onChange={(e) => updateField('reference_websites', e.target.value)}
                  placeholder="URLs de sitios web que te gustan o que son de tu competencia..."
                  rows={2}
                  className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors resize-none"
                  data-testid="input-references"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-500 mb-2">Instagram</label>
                  <input
                    type="text"
                    value={formData.instagram}
                    onChange={(e) => updateField('instagram', e.target.value)}
                    placeholder="@tunegocio"
                    className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors"
                    data-testid="input-instagram"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-500 mb-2">Facebook</label>
                  <input
                    type="text"
                    value={formData.facebook}
                    onChange={(e) => updateField('facebook', e.target.value)}
                    placeholder="facebook.com/tunegocio"
                    className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors"
                    data-testid="input-facebook"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-500 mb-2">Link de Google Maps</label>
                <input
                  type="url"
                  value={formData.google_maps_link}
                  onChange={(e) => updateField('google_maps_link', e.target.value)}
                  placeholder="https://maps.google.com/..."
                  className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors"
                  data-testid="input-maps"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-500 mb-2">Notas Adicionales</label>
                <textarea
                  value={formData.additional_notes}
                  onChange={(e) => updateField('additional_notes', e.target.value)}
                  placeholder="Cualquier otra información que quieras compartir con nosotros..."
                  rows={3}
                  className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors resize-none"
                  data-testid="input-notes"
                />
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
            {currentStep > 1 ? (
              <Button
                onClick={() => setCurrentStep(currentStep - 1)}
                variant="outline"
                className="border-white/20 hover:bg-white hover:text-black rounded-none"
                data-testid="prev-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Anterior
              </Button>
            ) : (
              <div />
            )}
            
            {currentStep < totalSteps ? (
              <Button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!isStepValid(currentStep)}
                className="bg-white text-black hover:bg-zinc-200 rounded-none disabled:opacity-50"
                data-testid="next-btn"
              >
                Siguiente
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading || !isStepValid(currentStep)}
                className="bg-white text-black hover:bg-zinc-200 rounded-none disabled:opacity-50"
                data-testid="submit-btn"
              >
                {loading ? 'Enviando...' : 'Enviar Información'}
              </Button>
            )}
          </div>
        </motion.div>

        {/* Help Text */}
        <p className="text-center text-sm text-zinc-600 mt-6">
          * Campos obligatorios. Tus datos están protegidos y serán usados únicamente para crear tu página web.
        </p>
      </div>
    </div>
  );
}
